import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Search, Check, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { PaymentLinkCard } from "../components/payments/PaymentLinkCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListState } from "../hooks/useListState";
import { formatCurrency, formatEpoch } from "../lib/utils";
import { useTaxConfig, breakdownFromPayment } from "../lib/tax";
import { GstInline } from "../components/payments/GstBreakdown";
import { paymentStatusMeta, paymentMethodLabel, paymentPurposeLabel } from "../lib/statusMaps";

const PAGE_SIZE = 20;
const STATUS_FILTERS = [
  { value: "all", label: "All Statuses" }, { value: "1", label: "Pending" },
  { value: "2", label: "Approved" }, { value: "3", label: "Declined" },
];
const PURPOSE_FILTERS = [
  { value: "all", label: "All Purposes" }, { value: "registration", label: "Registration" },
  { value: "renewal", label: "Renewal" },
];

export default function Payments() {
  const navigate = useNavigate();
  const [search, setSearch] = useListState("payments.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [statusFilter, setStatusFilter] = useListState("payments.statusFilter", "all");
  const [purposeFilter, setPurposeFilter] = useListState("payments.purposeFilter", "all");
  const [page, setPage] = useListState("payments.page", 0);
  const [dialog, setDialog] = useState(null); // { type: "approve"|"decline", payment }
  const [expanded, setExpanded] = useState(null);
  const taxCfg = useTaxConfig();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["payments-list", { q: debouncedSearch, statusFilter, purposeFilter, page }],
    queryFn: async () => {
      const { data } = await apiClient.get("/payments", {
        params: {
          q: debouncedSearch || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          purpose: purposeFilter !== "all" ? purposeFilter : undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const error = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load payments.") : null;

  // Reset to the first page whenever a filter/search changes.
  const filterKey = `${debouncedSearch}|${statusFilter}|${purposeFilter}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  // Patch a single payment row's status across every cached payments-list page
  // (safe selective update), then invalidate to reconcile derived fields/counts.
  const patchRowStatus = (paymentId, status) => {
    queryClient.setQueriesData({ queryKey: ["payments-list"] }, (old) =>
      old?.items
        ? { ...old, items: old.items.map((r) => (r.id === paymentId ? { ...r, status } : r)) }
        : old);
  };
  const invalidatePaymentViews = () => {
    queryClient.invalidateQueries({ queryKey: ["payments-list"] });
    queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
    queryClient.invalidateQueries({ queryKey: ["subscription-counts"] });
  };

  // Server-validated POST stays authoritative; useMutation only orchestrates
  // loading state + post-success cache updates.
  const sendLinkMutation = useMutation({
    mutationFn: (payment) => apiClient.post(`/payments/${payment.id}/link`),
    onSuccess: (_res, payment) => {
      toast.success("Payment link created and sent");
      setExpanded(payment.id);
      queryClient.invalidateQueries({ queryKey: ["payments-list"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not create a payment link.")),
  });

  const approveMutation = useMutation({
    mutationFn: (payment) => apiClient.post(`/payments/${payment.id}/approve`).then((r) => r.data),
    onSuccess: (data, payment) => {
      if (data.new_subscription_id) {
        toast.success(`Payment approved — renewal created (subscription #${data.new_subscription_id})`, {
          action: { label: "View", onClick: () => navigate(`/subscriptions/${data.new_subscription_id}`) },
        });
      } else if (data.activated) {
        toast.success("Payment approved — subscription activated");
      } else {
        toast.success("Payment approved");
      }
      patchRowStatus(payment.id, 2);
      invalidatePaymentViews();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not approve this payment.")),
    onSettled: () => setDialog(null),
  });

  const declineMutation = useMutation({
    mutationFn: (payment) => apiClient.post(`/payments/${payment.id}/decline`),
    onSuccess: (_res, payment) => {
      toast.success("Payment declined");
      patchRowStatus(payment.id, 3);
      invalidatePaymentViews();
    },
    onError: (err) => toast.error(getApiErrorMessage(err, "Could not decline this payment.")),
    onSettled: () => setDialog(null),
  });

  const columns = [
    { key: "id", header: "ID", className: "text-text-muted" },
    { key: "customer_name", header: "Customer", render: (r) => r.customer_name || `#${r.customer_id}` },
    { key: "subscription_id", header: "Subscription", render: (r) => r.subscription_id ? (
      <button className="text-primary hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/subscriptions/${r.subscription_id}`); }} data-testid={`payment-row-${r.id}-subscription-link`}>
        #{r.subscription_id}
      </button>
    ) : "—" },
    { key: "amout", header: "Amount (incl. GST)", subtitle: true, render: (r) => {
      const bd = breakdownFromPayment(r, taxCfg);
      return (
        <div>
          <GstInline bd={bd} testId={`payment-row-${r.id}-amount`} />
          {bd.hasDiscount && <div className="text-xs text-text-muted">{formatCurrency(bd.initial)} − {formatCurrency(bd.discount)} disc.</div>}
        </div>
      );
    } },
    { key: "payment_method_label", header: "Method", render: (r) => paymentMethodLabel(r.payment_method) },
    { key: "purpose", header: "Purpose", render: (r) => paymentPurposeLabel(r.purpose) },
    { key: "status", header: "Status", render: (r) => { const m = paymentStatusMeta(r.status, r.payment_method, r.collected_at, r.purpose, r.checkout_state); return <StatusBadge label={m.label} variant={m.variant} />; } },
    { key: "created_at", header: "Created", render: (r) => formatEpoch(r.created_at) },
    {
      key: "actions", header: "Actions", headerClassName: "text-right", className: "text-right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {r.payment_link && (
            <Button variant="outline" size="sm" onClick={() => setExpanded(expanded === r.id ? null : r.id)} data-testid={`payment-row-${r.id}-view-link-button`}>
              <Link2 size={13} strokeWidth={1.5} />
            </Button>
          )}
          {r.status === 1 && !r.payment_link && (
            <Button variant="outline" size="sm" onClick={() => sendLinkMutation.mutate(r)} disabled={sendLinkMutation.isPending && sendLinkMutation.variables?.id === r.id} data-testid={`payment-row-${r.id}-send-link-button`}>
              <Link2 size={13} strokeWidth={1.5} /> {sendLinkMutation.isPending && sendLinkMutation.variables?.id === r.id ? "Sending..." : "Send Link"}
            </Button>
          )}
          {r.status === 1 && (
            <>
              <Button variant="outline" size="sm" onClick={() => setDialog({ type: "approve", payment: r })} data-testid={`payment-row-${r.id}-approve-button`}>
                <Check size={13} strokeWidth={1.5} /> Approve
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDialog({ type: "decline", payment: r })} data-testid={`payment-row-${r.id}-decline-button`}>
                <X size={13} strokeWidth={1.5} /> Decline
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="payments-page">
      <PageHeader
        title="Payments"
        description="Registration and renewal payments — approve cash, share online links, or review history."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input className="pl-9" placeholder="Search customer name..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="payment-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="payment-status-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={purposeFilter} onValueChange={setPurposeFilter}>
          <SelectTrigger className="w-40" data-testid="payment-purpose-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PURPOSE_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <DataTable
        testId="payments-table"
        columns={columns}
        rows={items}
        loading={listQuery.isPending}
        error={error}
        onRetry={() => listQuery.refetch()}
        emptyTitle="No payments found"
        emptyDescription="Try adjusting your filters."
      />
      {expanded && items.find((r) => r.id === expanded)?.payment_link && (
        <div className="mt-3">
          <PaymentLinkCard link={items.find((r) => r.id === expanded).payment_link} testId={`payment-${expanded}-link-card`} />
        </div>
      )}
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testId="payments-pagination" />

      <ConfirmDialog
        open={dialog?.type === "approve"} onOpenChange={(v) => setDialog(v ? dialog : null)}
        title="Approve this payment?"
        description="This marks the payment Approved and activates the subscription (or completes the queued renewal) immediately."
        confirmLabel="Approve" variant="default" loading={approveMutation.isPending} onConfirm={() => approveMutation.mutate(dialog.payment)}
        testId="payment-approve-confirm-dialog"
      />
      <ConfirmDialog
        open={dialog?.type === "decline"} onOpenChange={(v) => setDialog(v ? dialog : null)}
        title="Decline this payment?"
        description="The subscription stays Pending — no activation or renewal happens."
        confirmLabel="Decline" variant="danger" loading={declineMutation.isPending} onConfirm={() => declineMutation.mutate(dialog.payment)}
        testId="payment-decline-confirm-dialog"
      />
    </div>
  );
}
